"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CircleCheck as CheckCircle2, Loader, Mail, Phone, ShieldCheck } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactRows = [{
  label: "New Partnerships",
  name: "Sales\xA0",
  contact: "+1 219-286-5115",
  icon: Phone
}];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border bg-card p-8">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="size-5 text-accent mt-1 shrink-0" />
          <div>
            <h3 className="font-default text-xl text-foreground font-semibold tracking-tight mb-2">
              Inquiry submitted.
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              {successMessage}
            </p>
            <Button onClick={resetForm} variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background">
              Send another inquiry
            </Button>
          </div>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <FormField control={control} name="contact_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-[0.16em] font-default font-semibold text-muted-foreground">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Name" className="bg-background border-b-2 border-border h-11 px-0 focus-visible:border-accent focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="institution_name" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-[0.16em] font-default font-semibold text-muted-foreground">
                Company Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Company" className="bg-background border-b-2 border-border h-11 px-0 focus-visible:border-accent focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-[0.16em] font-default font-semibold text-muted-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@company.com" className="bg-background border-b-2 border-border h-11 px-0 focus-visible:border-accent focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-[0.16em] font-default font-semibold text-muted-foreground">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 000-000-0000" className="bg-background border-b-2 border-border h-11 px-0 focus-visible:border-accent focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="asset_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-[0.16em] font-default font-semibold text-muted-foreground">
                Industry Sector
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background border-b-2 border-border h-11 px-0 w-full focus-visible:border-accent focus-visible:ring-0 focus-visible:ring-offset-0">
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Automotive">Automotive</SelectItem>
                  <SelectItem value="Metal Fabrication">Metal Fabrication</SelectItem>
                  <SelectItem value="Safety">Safety</SelectItem>
                  <SelectItem value="Shipbuilding">Shipbuilding</SelectItem>
                  <SelectItem value="Industrial Gas">Industrial Gas</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="aum" rules={{
        required: "Annual revenue is required",
        min: {
          value: 1000000,
          message: "Minimum revenue is $1,000,000"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-[0.16em] font-default font-semibold text-muted-foreground">
                Annual Revenue (USD)
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="0" className="bg-background border-b-2 border-border h-11 px-0 focus-visible:border-accent focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="text-xs text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="services_interest" rules={{
      required: "Please select at least one service"
    }} render={({
      field
    }) => {
      const services = ["Sales Representation", "Market Expansion", "Technical Consulting", "Channel Development", "Strategic Sourcing"];
      const selected: string[] = Array.isArray(field.value) ? field.value : [];
      const toggle = (val: string) => {
        if (selected.includes(val)) {
          field.onChange(selected.filter(s => s !== val));
        } else {
          field.onChange([...selected, val]);
        }
      };
      return <FormItem>
              <FormLabel className="text-xs uppercase tracking-[0.16em] font-default font-semibold text-muted-foreground">
                Services Required
              </FormLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                {services.map((service, i) => {
            const isChecked = selected.includes(service);
            return <label key={service} data-index={i} className="flex items-center gap-3 cursor-pointer group">
                      <span className={`size-4 border flex items-center justify-center rounded-none transition-colors ${isChecked ? "bg-accent border-accent" : "bg-background border-border group-hover:border-accent"}`}>
                        {isChecked && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="black" strokeWidth="1.5" strokeLinecap="square" />
                          </svg>}
                      </span>
                      <input type="checkbox" className="sr-only" checked={isChecked} onChange={() => toggle(service)} />
                      <span className="text-sm text-foreground">{service}</span>
                    </label>;
          })}
              </div>
              <FormMessage className="text-xs text-destructive mt-2" />
            </FormItem>;
    }} />

      {submitError && <div className="border border-destructive bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4 border-t border-border flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-xs text-muted-foreground leading-relaxed max-w-md">
          All inquiries are handled with strict confidentiality. A representative will contact you within one business day.
        </p>
        <Button type="submit" disabled={isSubmitting} className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-6 font-default font-semibold tracking-wide text-sm shrink-0">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : "Submit Inquiry"}
        </Button>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="20321" id="secure-your-representation" className="bg-background text-foreground border-t border-border py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="lg:col-span-5">
            <div className="w-12 h-px bg-border mb-6" />
            <p className="text-xs uppercase tracking-[0.16em] font-default font-semibold text-muted-foreground mb-6">&nbsp;CONTACT</p>
            <h2 className="font-default text-4xl md:text-5xl font-semibold text-foreground tracking-tight leading-[1.1] mb-7">
              Initiate your{" "}
              <span className="italic text-primary font-normal">partnership</span>.
            </h2>
            <p className="font-default text-lg text-muted-foreground leading-relaxed font-light mb-12 max-w-md">
              Define your operational requirements and market objectives. Our team will review your profile and provide a strategic framework for representation.
            </p>

            <div className="space-y-0">
              {contactRows.map((row, i) => {
              const Icon = row.icon;
              return <div key={row.label} data-index={i} className="group py-6 border-t border-border last:border-b transition-colors duration-300 hover:border-t-accent">
                    <div className="flex items-start gap-4">
                      <Icon className="size-4 text-muted-foreground mt-1 shrink-0" strokeWidth={1.25} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-[0.16em] font-default font-semibold text-muted-foreground mb-2">
                          {row.label}
                        </p>
                        <p className="font-default text-lg text-foreground leading-snug mb-1">
                          {row.name}
                        </p>
                        <p className="font-mono text-sm text-muted-foreground">
                          {row.contact}
                        </p>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="representation_form_contact" formId="representation_form_contact" sectionName="secure-your-representation" defaultValues={{
            institution_name: "",
            contact_name: "",
            email: "",
            phone: "",
            aum: "",
            asset_type: "",
            services_interest: []
          }} validationRules={{
            institution_name: {
              required: true
            },
            contact_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            aum: {
              required: true,
              min: 1000000
            },
            services_interest: {
              required: true
            }
          }} submitText="Submit Inquiry" successMessage="Your inquiry has been received. A senior partner will respond within one business day." errorMessage="Submission failed. Please verify your details or contact us directly.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}